import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, isIOS, isStandalone } from '@/lib/push/subscribe';

/**
 * A2HS Walkthrough — Family (CLAUDE.md §6 madde 10)
 *
 * Cross-platform "Add to Home Screen" coach. Three behaviors based on platform:
 *
 *   - iOS Safari (not standalone) → 3-step illustrated guide using the Safari
 *     share sheet. iOS does NOT expose a programmatic install prompt, so the
 *     coach is the only path.
 *   - Android Chrome / Edge        → captures `beforeinstallprompt` and offers
 *     a single "Yükle" button that triggers the native UA prompt.
 *   - Already standalone           → renders nothing.
 *
 * Dismissal: stored under `family.a2hs.dismissedAt` in localStorage. Re-shown
 * after 7 days so users who dismissed accidentally still get a second chance.
 *
 * Older user UX: large tap targets (min 56px), single primary action per
 * screen, plain language. No flag emojis (CLAUDE.md §7).
 *
 * iOS visual: each step gets a small inline SVG so users can match the icon
 * they see in Safari's chrome (share button) instead of hunting for the right
 * label. Crucial for the older audience — many of them call the share button
 * "the box with the arrow" rather than reading the menu names.
 *
 * Accessibility: dialog role, focus trap between primary/dismiss buttons, ESC
 * closes (acts as dismiss).
 */

const DISMISS_KEY = 'family.a2hs.dismissedAt';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function shouldShowAfterCooldown(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return true;
  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) return true;
  return Date.now() - dismissedAt > DISMISS_COOLDOWN_MS;
}

function persistDismissal(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  }
}

/** Safari iOS "Share" icon — square with up-arrow. Rendered at 24×24. */
function ShareIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Arrow */}
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      {/* Box */}
      <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
    </svg>
  );
}

/** Plus-in-square icon — matches iOS "Add to Home Screen" row in the share sheet. */
function PlusSquareIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M12 8v8" />
      <path d="M8 12h8" />
    </svg>
  );
}

/** Check-circle for the final "Add" confirmation step. */
function AddBadgeIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

interface StepProps {
  number: number;
  text: string;
  icon: React.ReactNode;
}

function Step({ number, text, icon }: StepProps) {
  return (
    <li className="flex gap-3 items-start">
      <span
        aria-hidden="true"
        className="flex-shrink-0 w-9 h-9 rounded-full bg-bg-bubble-sent text-accent-primary border border-accent-gold flex items-center justify-center font-serif italic text-lg"
      >
        {number}
      </span>
      <div className="flex-1 flex items-center gap-3">
        <span className="text-base text-text-primary">{text}</span>
        <span
          aria-hidden="true"
          className="flex-shrink-0 w-7 h-7 text-accent-gold-dark"
        >
          {icon}
        </span>
      </div>
    </li>
  );
}

export function A2HSWalkthrough() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const dismissRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Already a PWA → never show.
    if (isStandalone()) return;
    if (!shouldShowAfterCooldown()) return;

    // iOS Safari: no install event exists; just show the manual coach.
    if (isIOS()) {
      // Defer slightly so the coach doesn't punch in while the page is still
      // painting; keeps the first paint clean.
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }

    // Android Chrome/Edge: wait for the browser's prompt event.
    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setOpen(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  // Focus management — primary action gets focus, restore on close.
  useEffect(() => {
    if (!open) return;
    if (typeof document !== 'undefined') {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    }
    const id = window.setTimeout(() => {
      // Prefer the affirmative button when available (Android install);
      // otherwise fall back to the dismiss button so ESC equivalent exists.
      (primaryRef.current ?? dismissRef.current)?.focus();
    }, 50);
    return () => {
      window.clearTimeout(id);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // ESC closes (dismiss). Tab cycles between primary ↔ dismiss when both exist.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDismiss();
        return;
      }
      if (e.key !== 'Tab') return;
      const primary = primaryRef.current;
      const dismiss = dismissRef.current;
      // If only one button is present (iOS coach), let Tab no-op inside.
      if (!primary || !dismiss) {
        if (dismiss) {
          e.preventDefault();
          dismiss.focus();
        }
        return;
      }
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === primary) {
          e.preventDefault();
          dismiss.focus();
        }
      } else {
        if (active === dismiss) {
          e.preventDefault();
          primary.focus();
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function handleDismiss() {
    persistDismissal();
    setOpen(false);
  }

  async function handleAndroidInstall() {
    if (!installEvent) {
      handleDismiss();
      return;
    }
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
    } catch (err) {
      console.warn('a2hs-install-prompt-failed', err);
    }
    persistDismissal();
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="a2hs-title"
      aria-describedby="a2hs-body"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full md:max-w-md md:rounded-2xl rounded-t-2xl bg-bg-elevated border-t md:border border-accent-gold p-6 shadow-warm-lg">
        <p className="eyebrow mb-1">PWA</p>
        <h2 id="a2hs-title" className="font-serif italic text-2xl text-text-primary mb-4">
          {t('a2hs.title')}
        </h2>

        {isIOS() ? (
          <ol id="a2hs-body" className="space-y-4 mb-6">
            <Step
              number={1}
              text={t('a2hs.iosStep1')}
              icon={<ShareIcon className="w-7 h-7" />}
            />
            <Step
              number={2}
              text={t('a2hs.iosStep2')}
              icon={<PlusSquareIcon className="w-7 h-7" />}
            />
            <Step
              number={3}
              text={t('a2hs.iosStep3')}
              icon={<AddBadgeIcon className="w-7 h-7" />}
            />
          </ol>
        ) : (
          <p id="a2hs-body" className="mb-6 text-base text-text-primary">
            {t('a2hs.body')}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {isAndroid() && installEvent && (
            <button
              ref={primaryRef}
              type="button"
              onClick={() => void handleAndroidInstall()}
              className="w-full min-h-tap px-6 py-3 rounded-xl bg-accent-primary text-white text-base font-semibold border border-accent-gold shadow-warm-sm hover:bg-accent-primary/90 focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
            >
              {t('a2hs.androidPrompt')}
            </button>
          )}
          <button
            ref={dismissRef}
            type="button"
            onClick={handleDismiss}
            className="w-full min-h-tap px-6 py-3 rounded-xl border border-accent-gold text-base text-accent-gold-dark hover:bg-bg-bubble-received/40 focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 font-serif italic"
          >
            {t('a2hs.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
